import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { io, type Socket } from "socket.io-client";
import type {
  AckResult,
  ChatEntry,
  MatchOverPayload,
  MatchStartPayload,
  RoomSnapshot,
  ScoreSnapshot,
} from "./types";

const SERVER_URL =
  import.meta.env.VITE_SERVER_URL || "http://localhost:8080";

type ConnState = "idle" | "connecting" | "connected" | "disconnected";

interface SocketCtxValue {
  connState: ConnState;
  socketId: string | null;
  room: RoomSnapshot | null;
  matchStart: MatchStartPayload | null;
  scores: ScoreSnapshot | null;
  matchOver: MatchOverPayload | null;
  chat: ChatEntry[];
  opponentConnectivity: "ok" | "lost";
  createRoom: (name: string) => Promise<AckResult>;
  joinRoom: (code: string, name: string) => Promise<AckResult>;
  leaveRoom: () => Promise<void>;
  setReady: (ready: boolean) => Promise<AckResult>;
  reportDeath: () => void;
  sendChat: (message: string) => Promise<AckResult>;
  resetMatchArtifacts: () => void;
}

const SocketContext = createContext<SocketCtxValue | null>(null);

export function SocketProvider({ children }: PropsWithChildren) {
  const socketRef = useRef<Socket | null>(null);
  const [connState, setConnState] = useState<ConnState>("idle");
  const [socketId, setSocketId] = useState<string | null>(null);
  const [room, setRoom] = useState<RoomSnapshot | null>(null);
  const [matchStart, setMatchStart] = useState<MatchStartPayload | null>(null);
  const [scores, setScores] = useState<ScoreSnapshot | null>(null);
  const [matchOver, setMatchOver] = useState<MatchOverPayload | null>(null);
  const [chat, setChat] = useState<ChatEntry[]>([]);
  const [opponentConnectivity, setOpponentConnectivity] = useState<"ok" | "lost">("ok");

  useEffect(() => {
    setConnState("connecting");
    const s = io(SERVER_URL, {
      transports: ["websocket", "polling"],
      reconnectionAttempts: 8,
      reconnectionDelay: 800,
    });
    socketRef.current = s;

    s.on("connect", () => {
      setConnState("connected");
      setSocketId(s.id ?? null);
    });
    s.on("disconnect", () => setConnState("disconnected"));
    s.on("connect_error", () => setConnState("disconnected"));

    s.on("roomState", (snap: RoomSnapshot) => {
      setRoom(snap);
      if (snap.state === "lobby") {
        setMatchStart(null);
        setScores(null);
      }
    });
    s.on("matchStart", (payload: MatchStartPayload) => {
      setMatchOver(null);
      setMatchStart(payload);
      setScores(null);
    });
    s.on("matchGo", () => {
      // marker — engine starts on its own timer; nothing required here.
    });
    s.on("scoreSync", (snap: ScoreSnapshot) => setScores(snap));
    s.on("matchOver", (payload: MatchOverPayload) => {
      setMatchOver(payload);
    });
    s.on("opponentDied", () => {
      // could trigger an audio cue; HUD already updates via scoreSync.
    });
    s.on("opponentConnectivity", (p: { status: "lost" | "ok" }) =>
      setOpponentConnectivity(p.status)
    );
    s.on("receiveChat", (entry: ChatEntry) => {
      setChat((prev) => [...prev.slice(-99), { ...entry, self: entry.from === s.id }]);
    });

    return () => {
      s.removeAllListeners();
      s.disconnect();
      socketRef.current = null;
    };
  }, []);

  const ackPromise = useCallback(<T,>(event: string, payload: unknown): Promise<AckResult<T>> => {
    const s = socketRef.current;
    if (!s || !s.connected) {
      return Promise.resolve({ ok: false, error: "INTERNAL_ERROR" });
    }
    return new Promise((resolve) => {
      const timer = setTimeout(() => resolve({ ok: false, error: "INTERNAL_ERROR" }), 5000);
      s.emit(event, payload, (res: AckResult<T>) => {
        clearTimeout(timer);
        resolve(res ?? { ok: false, error: "INTERNAL_ERROR" });
      });
    });
  }, []);

  const createRoom = useCallback(
    (name: string) => ackPromise("createRoom", { name }).then(setRoomFromAck),
    [ackPromise]
  );
  const joinRoom = useCallback(
    (code: string, name: string) => ackPromise("joinRoom", { code, name }).then(setRoomFromAck),
    [ackPromise]
  );
  const leaveRoom = useCallback(async () => {
    await ackPromise("leaveRoom", {});
    setRoom(null);
    setMatchStart(null);
    setScores(null);
    setMatchOver(null);
    setChat([]);
  }, [ackPromise]);
  const setReady = useCallback(
    (ready: boolean) => ackPromise("playerReady", { ready }),
    [ackPromise]
  );
  const reportDeath = useCallback(() => {
    socketRef.current?.emit("playerDied", {});
  }, []);
  const sendChat = useCallback(
    (message: string) => ackPromise("chatMessage", { message }),
    [ackPromise]
  );

  function setRoomFromAck(res: AckResult): AckResult {
    if (res.ok && res.snapshot) setRoom(res.snapshot);
    return res;
  }

  const resetMatchArtifacts = useCallback(() => {
    setMatchOver(null);
    setMatchStart(null);
    setScores(null);
    setOpponentConnectivity("ok");
  }, []);

  const value = useMemo<SocketCtxValue>(
    () => ({
      connState,
      socketId,
      room,
      matchStart,
      scores,
      matchOver,
      chat,
      opponentConnectivity,
      createRoom,
      joinRoom,
      leaveRoom,
      setReady,
      reportDeath,
      sendChat,
      resetMatchArtifacts,
    }),
    [
      connState,
      socketId,
      room,
      matchStart,
      scores,
      matchOver,
      chat,
      opponentConnectivity,
      createRoom,
      joinRoom,
      leaveRoom,
      setReady,
      reportDeath,
      sendChat,
      resetMatchArtifacts,
    ]
  );

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

export function useSocket(): SocketCtxValue {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error("useSocket must be used inside <SocketProvider>");
  return ctx;
}
